import { useCallback, useEffect, useState } from "react";
import { containerOrderIntakeAPI } from "../services/api";
import { useSocketEvent } from "./useSocketEvent";
import type { IntakeConnectionHealth } from "../types/containerOrderIntake";

export const INTAKE_CHANGED_EVENT = "containerOrderIntake:changed";
export const OLIVER_CONNECTION_EVENT = "oliverConnection:changed";

/**
 * How many Oliver orders are waiting for review, and whether the line that
 * brings them in is up.
 *
 * Refetched whenever the server says the queue changed (an order arrived, or
 * somebody accepted or declined one) or the connection turned over, and on a
 * slow timer as a backstop for a socket that dropped while the tab was asleep.
 *
 * `connection` is null for everyone but an admin: the server decides who may
 * know, and simply does not send it to anybody else.
 */
export function useIntakeCounts(enabled = true) {
  const [pending, setPending] = useState(0);
  const [connection, setConnection] = useState<IntakeConnectionHealth | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await containerOrderIntakeAPI.counts();
      setPending(res.data.data.pending ?? 0);
      setConnection(res.data.data.connection ?? null);
    } catch {
      // A badge is not worth an error toast; the next refresh tries again.
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const timer = setInterval(refresh, 2 * 60 * 1000);
    return () => clearInterval(timer);
  }, [enabled, refresh]);

  useSocketEvent(INTAKE_CHANGED_EVENT, refresh, enabled);
  useSocketEvent(OLIVER_CONNECTION_EVENT, refresh, enabled);

  return { pending, connection, refresh };
}
